import { createJsonCodeBlock, InfoTable } from './main';

/**
 * Render the policy details in an accordion view with a single accordion-item per OU and account.
 *
 * @param {object} attachedPolicies contains an array with all directly attached and an object with OUs and attached policies
 * @param {string} accountId the target AWS account id
 * @param {object} sortedOrgUnits a sorted object with all OUs from root down to the last OU child of the account
 */
export function renderPolicyDetails(attachedPolicies, accountId, sortedOrgUnits) {
  /**
   * Get a single policy incl. metadata and the actual policy and render a single row per policy with an info table on the
   * left and the actual policy on the right.
   *
   * @param {object} policy the policy object with metadata and the actual policy document
   * @returns
   */
  function renderPolicy(policy) {
    // Parse the json policy and some color-coding for `Allow` and `Deny` statements
    const json = JSON.parse(policy['Content']);

    // Calculate the current size in bytes of the policy. This might not be 100% accurate but it's better than nothing.
    // Once calculated we add it to the policy's PolicySummary to show it below in the policy info table.
    // SCPs use the raised quota (L-C48BCE79, CLOUD-37213) of 10240 bytes, RCPs are still limited to 5120 bytes.
    const maxSizeInBytes = policy['PolicySummary']['Type'] === 'SERVICE_CONTROL_POLICY' ? 10240 : 5120;
    const policySizeInBytes = new TextEncoder().encode(JSON.stringify(json)).length;
    const policyUsage = (policySizeInBytes / maxSizeInBytes) * 100;
    policy['PolicySummary']['Size'] =
      `~${policySizeInBytes} bytes of max. ${maxSizeInBytes} bytes (${policyUsage.toFixed(2)}%)`;

    const jsonDump = createJsonCodeBlock(json);

    const policyTypeLabels = {
      SERVICE_CONTROL_POLICY: 'SCP',
      RESOURCE_CONTROL_POLICY: 'RCP',
    };
    const policyTypeLabel = policyTypeLabels[policy['PolicySummary']['Type']] ?? policy['PolicySummary']['Type'];

    // Render and return the row item (=a single policy as mentioned above)
    return (
      <div class="row">
        <div class="detailsContent">
          <h5>
            {policyTypeLabel && `${policyTypeLabel} - `}
            {policy['PolicySummary']['Name']} <small>{policy['PolicySummary']['Description']}</small>
          </h5>

          <div class="row">
            {/* Create the info table on the left side of the panel which contains some metadata about the policy */}
            <div class="col-lg-12 col-xl-6">
              <InfoTable
                data={policy['PolicySummary']}
                rowInfos={[
                  { Name: 'Policy Type', Value: 'Type' },
                  { Name: 'Policy Name', Value: 'Name' },
                  { Name: 'Policy Id', Value: 'Id' },
                  { Name: 'Policy Arn', Value: 'Arn' },
                  { Name: 'Policy Description', Value: 'Description' },
                  { Name: 'AWS Managed Policy', Value: 'AwsManaged' },
                  { Name: 'Policy Size (bytes)', Value: 'Size' },
                ]}
              />
            </div>

            {/* Create the code snippet on the right side of the panel which contains the rendered policy itself */}
            <div class="col-lg-12 col-xl-6">{jsonDump}</div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render a single accordion-item incl. accordion-header and accordion-body. A single item will represent an
   * Organizational Unit or an AWS account incl. all policies which are assigned to the current resource.
   *
   * @param {string} id the OU or account id to create unique ids
   * @param {string} headerText the text value for the accordion-header button
   * @param {Array} renderedPolicies the actual content of the accordion-body (table with policy info and the policies themselves)
   * @param {number} scpCount number of SERVICE_CONTROL_POLICY policies attached
   * @param {number} rcpCount number of RESOURCE_CONTROL_POLICY policies attached
   * @returns
   */
  function renderAccordionItem(id, headerText, renderedPolicies, scpCount, rcpCount) {
    // Show a green badge if 3 or less are assigned, yellow if 4 are assigned, red if 5 are assigned.
    // SCPs and RCPs each have a hard limit of 5 per OU and AWS account, so they are colored independently.
    const getBadgeColor = count => {
      if (count == 4) return 'badge-warning text-dark';
      if (count > 4) return 'badge-danger text-dark';
      return 'badge-success';
    };
    const scpBadgeColor = getBadgeColor(scpCount);
    const rcpBadgeColor = getBadgeColor(rcpCount);

    // Render and return the actual accordion-item. The accordion will be open/visible (="collapse show") if there are
    // more than 2 policies associated as AWS is attaching the SCP FullAWSAccess and RCPFullAWSAccess policies by default to every OU and account.
    return (
      <div class="accordion-item">
        <h4 class="accordion-header" id={'accordionHeader-' + id}>
          <button
            class={'accordion-button' + (renderedPolicies.length > 2 ? '' : ' collapsed')}
            type="button"
            data-bs-toggle="collapse"
            data-bs-target={'#accordionBody-' + id}
            aria-expanded={renderedPolicies.length > 2 ? 'true' : 'false'}
            aria-controls={'accordionBody-' + id}>
            {headerText}
            <span class={'m-2 badge ' + scpBadgeColor}>{scpCount} from max. 5 SCPs attached</span>
            <span class={'m-2 badge ' + rcpBadgeColor}>{rcpCount} from max. 5 RCPs attached</span>
          </button>
        </h4>
        <div
          class={'accordion-collapse' + (renderedPolicies.length > 2 ? ' collapse show' : ' collapse')}
          id={'accordionBody-' + id}
          aria-labelledby={'accordionHeader-' + id}>
          <div class="accordion-body">
            <div>{renderedPolicies}</div>
          </div>
        </div>
      </div>
    );
  }

  // Generate an array with accordion-items per Organizational Unit
  const mappedOrgUnits = [];
  const policyOrgUnits = [];
  sortedOrgUnits.forEach(orgUnit => {
    const ouId = orgUnit.org_ou_id;
    const headerText = `Organizational Unit - ${orgUnit.org_ou_name} (${ouId})`;
    const policies = attachedPolicies?.ou_attached_policies[ouId]?.policies ?? [];
    const scpCount = policies.filter(s => s['PolicySummary']['Type'] === 'SERVICE_CONTROL_POLICY').length;
    const rcpCount = policies.filter(s => s['PolicySummary']['Type'] === 'RESOURCE_CONTROL_POLICY').length;
    const renderedPolicies = policies.map(policy => renderPolicy(policy));
    policyOrgUnits.push(renderAccordionItem(ouId, headerText, renderedPolicies, scpCount, rcpCount));
    mappedOrgUnits.push(ouId);
  });

  // Update an array with accordion-items per Organizational Unit that are not listed in the `sortedOrgUnits`
  const unassignedOus = Object.keys(attachedPolicies?.ou_attached_policies).filter(x => !mappedOrgUnits.includes(x));
  unassignedOus.forEach(ouId => {
    const headerText = `Organizational Unit - ${ouId}`;
    const policies = attachedPolicies?.ou_attached_policies[ouId]?.policies ?? [];
    const scpCount = policies.filter(s => s['PolicySummary']['Type'] === 'SERVICE_CONTROL_POLICY').length;
    const rcpCount = policies.filter(s => s['PolicySummary']['Type'] === 'RESOURCE_CONTROL_POLICY').length;
    const renderedPolicies = policies.map(policy => renderPolicy(policy));
    policyOrgUnits.push(renderAccordionItem(ouId, headerText, renderedPolicies, scpCount, rcpCount));
  });

  // Generate the last accordion-item for the AWS account itself
  const policies = attachedPolicies?.directly_attached_policies ?? [];
  const scpCount = policies.filter(s => s['PolicySummary']['Type'] === 'SERVICE_CONTROL_POLICY').length;
  const rcpCount = policies.filter(s => s['PolicySummary']['Type'] === 'RESOURCE_CONTROL_POLICY').length;
  const renderedPolicies = policies.map(policy => renderPolicy(policy));
  const policyAccount = renderAccordionItem(
    accountId,
    `AWS Account - ${accountId}`,
    renderedPolicies,
    scpCount,
    rcpCount,
  );

  // Create a new container with an accordion and all OU and account accordion-items
  const accordion = (
    <div class="detailsContent mb-0">
      <div class="accordion" id={'policies-by-ou-' + accountId}>
        {policyOrgUnits}
        {policyAccount}
      </div>
    </div>
  );

  // Delete the current children of the wrapper and append the new accordion to avoid that the content is stacking up
  $('#account_details_policies_' + accountId)
    .empty()
    .append(accordion);
}
